import React from 'react';
import './App.css';
import './styles/themes.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import ToastProvider from './components/ToastProvider';
import ScrollToTop from './components/ScrollToTop';
import ThemeLoader from './components/ThemeLoader';
import AppShell from './components/AppShell';
import UserLogin from './pages/UserLogin';
import Dashboard from './pages/Dashboard';
import Progress from './pages/Progress';
import Library from './pages/Library';
import Discover from './pages/Discover';
import Reader from './pages/Reader';
import Player from './pages/Player';
import BuyContentGate from './components/BuyContentGate';
import WriterLogin from './pages/WriterLogin';
import WriterDashboard from './pages/WriterDashboard';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminPanel from './pages/AdminPanel';
import AdminActivity from './pages/AdminActivity';
import KingDashboard from './pages/KingDashboard';
import WriterJourney from './pages/WriterJourney';
import WriterJourneyBreakdown from './pages/WriterJourneyBreakdown';
import ReaderJourneyBreakdown from './pages/ReaderJourneyBreakdown';
import ReaderJourney from './pages/ReaderJourney';
import ReaderTraining from './pages/ReaderTraining';
import ManageContent from './pages/ManageContent';
import Improve from './pages/Improve';
import AdminBreakdown from './pages/AdminBreakdown';
import UploadContent from './pages/UploadContent';
import UploadHub from './pages/UploadHub';
import UploadAudioSeries from './pages/UploadAudioSeries';
import UploadEpisodes from './pages/UploadEpisodes';
import UploadAudiobookSeries from './pages/UploadAudiobookSeries';
import SpeedTest from './pages/SpeedTest';
import QuizDemo from './pages/QuizDemo';
import MyLibrary from './pages/MyLibrary';
import MyLibraryBook from './pages/MyLibraryBook';
import UploadTextSeries from './pages/UploadTextSeries';
import TextSeriesDetail from './pages/TextSeriesDetail';
import TextSeriesReader from './pages/TextSeriesReader';
import VoiceMinutes from './pages/VoiceMinutes';
import DownloadedContent from './pages/DownloadedContent';
import SecretVault from './pages/SecretVault';
import Profile from './pages/Profile';
import Premium from './pages/Premium';
import Shop from './pages/Shop';
import PaymentCallback from './pages/PaymentCallback';
import StatsPage from './pages/StatsPage';
import PaymentsSuccess from './pages/PaymentsSuccess';
import PaymentsCancelled from './pages/PaymentsCancelled';
import ForgotPassword from './pages/ForgotPassword';
import WriterAssessment from './pages/WriterAssessment';
import ReadingCoursesHub from './pages/ReadingCoursesHub';
import LessonPlayer from './pages/LessonPlayer';
import WritingCoursesHub from './pages/WritingCoursesHub';
import WritingLessonPlayer from './pages/WritingLessonPlayer';
import BookClub from './pages/BookClub';
import SeriesPage from './pages/SeriesPage';
import Inbox from './pages/Inbox';
import Help from './pages/Help';
import ProfileStats from './pages/ProfileStats';
import ProfileNotifications from './pages/ProfileNotifications';
import ProfileSecurity from './pages/ProfileSecurity';
import ProfileEarnings from './pages/ProfileEarnings';
import ProfileAccount from './pages/ProfileAccount';
import ProfileAchievements from './pages/ProfileAchievements';
import ProfileFollowedAuthors from './pages/ProfileFollowedAuthors';
import WriterProfile from './pages/WriterProfile';
import BookClubRooms from './pages/BookClubRooms';
import QuestBoard from './pages/QuestBoard';
import AdminQuestReview from './pages/AdminQuestReview';
import AdminEditGrantsReview from './pages/AdminEditGrantsReview';
import BookSeries from './pages/BookSeries';
import AcademicLibrary from './pages/AcademicLibrary';
import Learn from './pages/Learn';
import LearnChunking from './pages/LearnChunking';
import LearnPacer from './pages/LearnPacer';
import LearnTimedQuiz from './pages/LearnTimedQuiz';
import LearnRegression from './pages/LearnRegression';
import LearnPreviewRead from './pages/LearnPreviewRead';
import LearnScan from './pages/LearnScan';
import LearnFixations from './pages/LearnFixations';
import LearnVocab from './pages/LearnVocab';
import LearnCourses from './pages/LearnCourses';
import LearnCourse from './pages/LearnCourse';
import AudioSeries from './pages/AudioSeries';
import BankPage from './pages/BankPage';
import InstallPWA from './components/InstallPWA';
import SwUpdateBanner from './components/SwUpdateBanner';
import PageErrorBoundary from './components/PageErrorBoundary';
import { AudioPlayerProvider } from './contexts/AudioPlayerContext';
import MiniPlayerBar from './components/MiniPlayerBar';
import SleepResumeBanner from './components/SleepResumeBanner';

// Helper to render a page wrapped in the consistent AppShell chrome.
// Every authed page is wrapped in a PageErrorBoundary so a render
// exception in any panel surfaces a friendly fallback (with one-tap
// auto-recovery for iOS Safari stale-bundle races) instead of a blank
// white screen — the recurring symptom users kept reporting.
const Shell = ({ children, ...props }) => (
  <AppShell {...props}>
    <PageErrorBoundary>{children}</PageErrorBoundary>
  </AppShell>
);

// Wrappers that gate the Reader / Player on the new coin paywall (BuyContentGate).
// useParams must run inside a Routes child, so we factor these out instead of
// inlining into the Route element.
const GatedReader = () => {
  const { bookId } = useParams();
  return (
    <BuyContentGate contentId={bookId}>
      <Reader />
    </BuyContentGate>
  );
};
const GatedPlayer = () => {
  const { bookId } = useParams();
  return (
    <BuyContentGate contentId={bookId}>
      <Player />
    </BuyContentGate>
  );
};

// Preserve ?ref=<code> query when bouncing through "/" → "/login" so
// the referral attribution survives the redirect (Jun 3 2026 spec).
function RootRedirect() {
  const loc = useLocation();
  return <Navigate to={`/login${loc.search || ''}`} replace />;
}

function AppRouter() {
  const location = useLocation();

  return (
    <Routes>
      {/* Public / auth routes — no shell */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<UserLogin />} />
      {/* Public Speed Reader Challenge — no auth required (anonymous
          users still play, XP is credited only when logged in). The
          post-login one-shot redirect lands on /speed-test?source=postlogin. */}
      <Route path="/speed-test" element={<SpeedTest />} />
      {/* Public Flash-Read + Retention Quiz — anonymous play, XP only
          credited when logged in. Mirrors /speed-test gating. */}
      <Route path="/quiz-demo" element={<QuizDemo />} />
      <Route path="/writer-login" element={<WriterLogin />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/payment/callback" element={<PaymentCallback />} />
      <Route path="/payments/success" element={<Shell><PaymentsSuccess /></Shell>} />
      <Route path="/payments/cancelled" element={<Shell><PaymentsCancelled /></Shell>} />
      <Route path="/stats" element={<Shell><StatsPage /></Shell>} />

      {/* Reader pages (role-aware shell resolves role from /api/auth/me) */}
      <Route path="/dashboard" element={<Shell><Dashboard /></Shell>} />
      <Route path="/progress" element={<Shell><Progress /></Shell>} />
      <Route path="/library" element={<Shell><Library /></Shell>} />
      {/* Premium-only personal library (Type / Paste / Scan) — visible
          ONLY to the owner; backend filters by user_id. */}
      <Route path="/my-library" element={<Shell><MyLibrary /></Shell>} />
      <Route path="/my-library/book/:bookId" element={<Shell><MyLibraryBook /></Shell>} />
      {/* Feb 9 2026 — Text Series (third upload option). */}
      <Route path="/upload-text-series" element={<Shell><UploadTextSeries /></Shell>} />
      <Route path="/text-series/:seriesId" element={<Shell><TextSeriesDetail /></Shell>} />
      <Route path="/text-series/:seriesId/episode/:episodeId" element={<Shell><TextSeriesReader /></Shell>} />
      <Route path="/voice-minutes" element={<Shell><VoiceMinutes /></Shell>} />
      <Route path="/discover" element={<Shell><Discover /></Shell>} />
      <Route path="/profile" element={<Shell><Profile /></Shell>} />
      <Route path="/premium" element={<Shell><Premium /></Shell>} />
      <Route path="/shop" element={<Shell><Shop /></Shell>} />
      <Route path="/offline-library" element={<Shell><DownloadedContent /></Shell>} />
      <Route path="/vault" element={<Shell><SecretVault /></Shell>} />

      {/* Reading Courses */}
      <Route path="/reading-course" element={<Shell><ReadingCoursesHub /></Shell>} />
      <Route path="/reading-course/lesson/:lessonId" element={<Shell><LessonPlayer /></Shell>} />

      {/* Immersive — keep top bar, hide bottom bar */}
      <Route path="/reader/:bookId" element={<Shell hideBottomBar><GatedReader /></Shell>} />
      <Route path="/player/:bookId" element={<Shell hideBottomBar><GatedPlayer /></Shell>} />

      {/* Writer pages */}
      <Route path="/writer-assessment" element={<WriterAssessment />} />
      <Route path="/writer-dashboard" element={<Shell role="writer"><WriterDashboard /></Shell>} />
      <Route path="/writer-courses" element={<Shell role="writer"><WritingCoursesHub /></Shell>} />
      <Route path="/writer-courses/lesson/:lessonId" element={<Shell role="writer"><WritingLessonPlayer /></Shell>} />
      <Route path="/upload" element={<Shell><UploadHub /></Shell>} />
      <Route path="/upload-text-ebook" element={<Shell><UploadContent /></Shell>} />
      <Route path="/upload-audio-series" element={<Shell><UploadAudioSeries /></Shell>} />
      <Route path="/upload-episodes" element={<Shell><UploadEpisodes /></Shell>} />
      <Route path="/upload-audiobook-series" element={<Shell><UploadAudiobookSeries /></Shell>} />
      <Route path="/book-club" element={<Shell><BookClub /></Shell>} />
      <Route path="/series/:seriesKey" element={<Shell><SeriesPage /></Shell>} />
      <Route path="/inbox" element={<Shell><Inbox /></Shell>} />
      <Route path="/help" element={<Shell><Help /></Shell>} />
      <Route path="/profile/stats" element={<Shell><ProfileStats /></Shell>} />
      <Route path="/profile/notifications" element={<Shell><ProfileNotifications /></Shell>} />
      <Route path="/profile/security" element={<Shell><ProfileSecurity /></Shell>} />
      <Route path="/profile/earnings" element={<Shell><ProfileEarnings /></Shell>} />
      <Route path="/profile/account" element={<Shell><ProfileAccount /></Shell>} />
      <Route path="/profile/achievements" element={<Shell><ProfileAchievements /></Shell>} />
      <Route path="/profile/followed-authors" element={<Shell><ProfileFollowedAuthors /></Shell>} />
      <Route path="/writer/:userId" element={<Shell><WriterProfile /></Shell>} />
      <Route path="/club" element={<Shell><BookClubRooms /></Shell>} />
      <Route path="/quests" element={<Shell><QuestBoard /></Shell>} />
      <Route path="/book-series" element={<Shell><BookSeries /></Shell>} />
      <Route path="/academic-library" element={<Shell><AcademicLibrary /></Shell>} />
      <Route path="/learn" element={<Shell><Learn /></Shell>} />
      <Route path="/learn/chunking" element={<Shell><LearnChunking /></Shell>} />
      <Route path="/learn/pacer" element={<Shell><LearnPacer /></Shell>} />
      <Route path="/learn/timed-quiz" element={<Shell><LearnTimedQuiz /></Shell>} />
      <Route path="/learn/regression" element={<Shell><LearnRegression /></Shell>} />
      <Route path="/learn/preview-read" element={<Shell><LearnPreviewRead /></Shell>} />
      <Route path="/learn/scan" element={<Shell><LearnScan /></Shell>} />
      <Route path="/learn/fixations" element={<Shell><LearnFixations /></Shell>} />
      <Route path="/learn/vocab" element={<Shell><LearnVocab /></Shell>} />
      <Route path="/learn/courses" element={<Shell><LearnCourses /></Shell>} />
      <Route path="/learn/courses/:courseId" element={<Shell><LearnCourse /></Shell>} />
      <Route path="/audio-series" element={<Shell><AudioSeries /></Shell>} />
      <Route path="/bank" element={<Shell><BankPage /></Shell>} />
      <Route path="/admin/quest-review" element={<Shell role="admin"><AdminQuestReview /></Shell>} />
      <Route path="/admin/edit-grants" element={<Shell><AdminEditGrantsReview /></Shell>} />

      {/* Admin pages */}
      <Route path="/admin" element={<Shell role="admin"><AdminDashboard /></Shell>} />
      <Route path="/admin-panel" element={<Shell role="admin"><AdminPanel /></Shell>} />
      <Route path="/admin/activity" element={<Shell role="admin"><AdminActivity /></Shell>} />
      <Route path="/king" element={<Shell><KingDashboard /></Shell>} />
      <Route path="/writer-journey" element={<Shell role="writer"><WriterJourney /></Shell>} />
      <Route path="/writer-journey/breakdown" element={<Shell role="writer"><WriterJourneyBreakdown /></Shell>} />
      <Route path="/reader-journey/breakdown" element={<Shell><ReaderJourneyBreakdown /></Shell>} />
      {/* Top-level Journey to Top Reader (mirrors /writer-journey) —
          Feb 9 2026 user spec. */}
      <Route path="/reader-journey" element={<Shell><ReaderJourney /></Shell>} />
      {/* Reading Training program — flash + chunk reading with real
          XP economy (+10 passage, +10 correct, -5 wrong). Feb 9 2026. */}
      <Route path="/reader-training" element={<Shell><ReaderTraining /></Shell>} />
      <Route path="/manage-content" element={<Shell role="admin"><ManageContent /></Shell>} />
      <Route path="/improve" element={<Shell><Improve /></Shell>} />
      <Route path="/admin/breakdown/:section" element={<Shell role="admin"><AdminBreakdown /></Shell>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ToastProvider>
          <AudioPlayerProvider>
            <ThemeLoader />
            <InstallPWA />
            <SwUpdateBanner />
            <ScrollToTop />
            <AppRouter />
            <SleepResumeBanner />
            <MiniPlayerBar />
          </AudioPlayerProvider>
        </ToastProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
